/**
 * How the front page ranks the last 24 hours. One formula, used by the browser when it sets
 * the page by hand (sessionApi.composeEdition) and mirrored line for line in the worker's
 * GET /edition (worker/src/cards.ts), so the page reads the same whichever set it.
 *
 * What leads: the story the desk scored highest for trend, lifted by what readers are doing
 * with it right now. A breaking score helps but cannot carry a story on its own; a fire in a
 * suburb breaks, it does not lead. Politics and money get a small lean because that is what
 * the country argues about; sport and celebrity a small drag, because they fill the page
 * otherwise. The curve on the trend score is convex: a 4 is worth a quarter of a 9, not half.
 *
 * The page moves with the day (USER 2026-10-30, "still the same from yesterday"): the clock
 * counts for most of a point and falls off a cliff around nine hours, and readers from last
 * night count for less than readers this morning, so the noon page is the morning's stories
 * and not the evening before's most-read.
 */

export interface RankSignals {
  /** The desk's 1..10 trend score. */
  trending: number;
  /** The desk's 1..10 breaking score. */
  breaking: number;
  /** Discussion buzz 1..3 from the pipeline (1 almost everywhere). */
  buzz: number;
  /** Hours since the story was published. */
  ageH: number;
  /** Readers through it in the window and their weighted events; 0 when unknown. */
  readers: number;
  weight: number;
  /** Supporting sources plus the primary one. */
  sources: number;
  insight: boolean;
  au: boolean;
  picture: boolean;
  category: string;
}

/** The lean per subject: the country argues about power and money. */
export const CATEGORY_LEAN: Record<string, number> = {
  "Power & Policy": 0.2,
  "Money & Work": 0.2,
  "Planet & Environment": 0.05,
  "Science & Systems": 0.05,
  "Technology & Tools": 0,
  "Culture & Curiosity": -0.1,
  "Sport & Play": -0.2,
};

export function editionScore(s: RankSignals): number {
  const trend = Math.min(10, Math.max(0, s.trending)) / 10;
  const brk = Math.min(10, Math.max(0, s.breaking)) / 10;
  const editorial = Math.pow(trend, 1.5) * 1.6 + brk * 0.45 + (s.breaking >= 7 && s.ageH < 6 ? 0.2 : 0);
  const heat = (s.readers > 0 ? Math.min(1, Math.log1p(s.readers * 1.5 + s.weight) / Math.log1p(60)) : 0) * Math.max(0.25, Math.exp(-s.ageH / 12));
  // The clock is a cliff around nine hours, not a slope: this morning's stories keep nearly
  // all of their point, last night's keep almost none, and two hours between two stories
  // from the same morning is worth little.
  const fresh = 0.9 / (1 + Math.exp((s.ageH - 9) / 2.5));
  const depth = (Math.min(s.sources, 4) / 4) * 0.25 + (s.insight ? 0.1 : 0);
  const buzz = Math.min(1, Math.max(0, (s.buzz - 1) / 2)) * 0.15;
  const lean = CATEGORY_LEAN[s.category] ?? 0;
  return editorial + heat + fresh + depth + buzz + (s.au ? 0.3 : 0) + lean + (s.picture ? 0.1 : 0);
}

/** The page from the ranked pool: no subject more than a third of it, the lead with a
 *  picture. Returns the chosen items in page order. */
export function pickEdition<T extends { score: number; category: string; picture: boolean }>(ranked: T[], limit: number): T[] {
  const sorted = [...ranked].sort((a, b) => b.score - a.score);
  const cap = Math.max(3, Math.ceil(limit / 3));
  const perCat = new Map<string, number>();
  const picked: T[] = [];
  const spare: T[] = [];
  for (const s of sorted) {
    const n = perCat.get(s.category) ?? 0;
    if (picked.length < limit && n < cap) {
      picked.push(s);
      perCat.set(s.category, n + 1);
    } else spare.push(s);
  }
  for (const s of spare) {
    if (picked.length >= limit) break;
    picked.push(s);
  }
  const leadAt = picked.findIndex((s) => s.picture);
  if (leadAt > 0) picked.unshift(...picked.splice(leadAt, 1));
  return picked;
}

/** What a story may wear on the page: at most one of these, in this order. */
export function editionMark(s: { breaking: number; ageH: number; hotRank: number; readers: number }): { breaking: boolean; hot: boolean; justIn: boolean } {
  return {
    breaking: s.breaking >= 7 && s.ageH < 4,
    hot: s.hotRank >= 0 && s.hotRank < 5 && s.readers >= 2,
    justIn: s.ageH < 2.5,
  };
}
